import { CompletionsOverTimeReport } from '@/client/dashboard/types';
import { VulnerableUsersOverTimeReport } from '@/client/reports';
import { TranslationFunctionType } from '@/common/types';
import { getDepartmentsPerformanceChartData } from '@/utils/helpers/analytics.helper';
import {
  colorBlueDark,
  colorGrayPale,
  colorGreenRadarDarkBorder,
  colorGreenRadarLightBorder,
  colorGreenRadarMiddleBorder,
  colorPurpleDark,
  colorRedMain,
  mediumPadding,
  platformFontFamily,
  xSmallFontSize,
  xSmallPadding,
  xxxSmallBorderRadius,
} from '@/utils/helpers/css-variables.helper';
import { getGroupedOverdueCoursesChartData } from '@/utils/helpers/dashboard.helper';
import { Tooltip } from 'chart.js';
import React from 'react';
import { flushSync } from 'react-dom';
import { createRoot, Root } from 'react-dom/client';
import styled from 'styled-components';

const CustomTooltipComponent = styled.div<{ isRadar?: boolean }>`
  max-width: ${({ isRadar }) => (isRadar ? '200px' : '100%')};
  overflow: hidden;
  background: var(--white-main);
  border: 1px solid var(--gray-pale);
  border-radius: var(--small-border-radius);
  padding: var(--medium-padding);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-size: var(--small-font-size);
  font-family: var(--font-family);

  .section {
    margin-bottom: var(--small-padding);

    &:last-child {
      margin-bottom: 0;
    }
  }

  .title {
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);
    font-weight: 500;
    margin-bottom: var(--xsmall-padding);
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    white-space: normal;
  }

  .item {
    font-size: var(--small-font-size);
    font-weight: 500;
    display: flex;
    align-items: center;
    margin-top: var(--xsmall-padding);
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    white-space: normal;
  }

  .dot {
    width: var(--medium-padding);
    height: var(--medium-padding);
    border-radius: 50%;
    margin-right: var(--xsmall-padding);
  }

  .dotted {
    border: var(--xxxsmall-padding) dotted;
    background: transparent;
  }
`;

interface TooltipData {
  title: string;
  dataPoints: Array<{
    label: string;
    value: number | null;
    color: string;
    type: 'bar' | 'line';
  }>;
  emailsSent?: number;
  t: TranslationFunctionType;
  isRadar?: boolean;
  showPercentage?: boolean;
  isOverdueTraining?: boolean;
}

const TooltipContent: React.FC<TooltipData> = ({
  title,
  dataPoints,
  emailsSent,
  t,
  isRadar,
  showPercentage = true,
  isOverdueTraining = false,
}) => {
  return (
    <CustomTooltipComponent isRadar={isRadar}>
      {dataPoints.length > 0 && (
        <div className="section">
          <div className="title">
            {t('phishing.analytics.resultLabel', { metric: title })}
          </div>
          {emailsSent !== undefined && (
            <div className="item">
              {t('phishing.analytics.emailsSent')}:{' '}
              {emailsSent.toLocaleString()}
            </div>
          )}
          {dataPoints.find((item) => item.value === null) ? (
            <div className="item">
              <div style={{ backgroundColor: 'transparent' }} />
              {isOverdueTraining
                ? t('awareness.analytics.noOverdueTraining')
                : t('phishing.analytics.noCampaign')}
            </div>
          ) : (
            dataPoints.map((item, i) => (
              <div key={i} className="item">
                <div className="dot" style={{ backgroundColor: item.color }} />
                {item.label}: {parseFloat(Number(item.value).toFixed(2))}
                {showPercentage ? '%' : ''}
              </div>
            ))
          )}
        </div>
      )}
      {dataPoints.length === 0 && (
        <div className="section">
          <div className="title">{title} results</div>
          <div className="item">
            <div style={{ backgroundColor: 'transparent' }} />
            {t('phishing.analytics.noSignData')}
          </div>
        </div>
      )}
    </CustomTooltipComponent>
  );
};

const tooltipRoots = new Map<HTMLElement, Root>();

export const hideTooltipForNavigation = (tooltipId: string) => {
  const tooltipEl = document.getElementById(tooltipId) as HTMLElement | null;
  if (tooltipEl) {
    tooltipEl.style.opacity = '0';
    tooltipEl.style.display = 'none';

    const existingRoot = tooltipRoots.get(tooltipEl);
    if (existingRoot) {
      existingRoot.unmount();
      tooltipRoots.delete(tooltipEl);
    }

    tooltipEl.remove();
  }
};

export const createCustomTooltip = (
  context: any,
  t: TranslationFunctionType,
  datasets?: any[],
  isRadar?: boolean,
  showPercentage = true,
  isLineChart = false,
  isOverdueTraining = false,
) => {
  const { chart, tooltip } = context;

  if (!chart?.canvas?.parentNode) return;

  const tooltipId = `chart-tooltip-${chart.canvas.id || 'default'}`;
  let tooltipEl = document.getElementById(tooltipId) as HTMLElement | null;

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = tooltipId;
    tooltipEl.className = 'custom-tooltip';
    tooltipEl.style.position = 'fixed';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.zIndex = '10000';
    tooltipEl.style.opacity = '0';
    document.body.appendChild(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    const existingRoot = tooltipRoots.get(tooltipEl);
    if (existingRoot) {
      existingRoot.unmount();
      tooltipRoots.delete(tooltipEl);
    }
    return;
  }

  if (tooltip.body) {
    const title = tooltip.title?.[0] || '';
    const dataPointIndex = tooltip.dataPoints[0]?.dataIndex;

    const emailsSent =
      datasets && dataPointIndex !== undefined
        ? datasets[dataPointIndex]?.value?.sentCount
        : undefined;

    const dataPoints = tooltip.dataPoints.map((dataPoint: any) => {
      const dataset = chart.data.datasets[dataPoint.datasetIndex];

      return {
        label: dataset.label,
        value: isRadar ? dataset.data[dataPoint.dataIndex] : dataPoint.parsed.y,
        color: isRadar
          ? dataset.backgroundColor[dataPoint.datasetIndex]
          : dataset.type === 'line'
          ? dataset.borderColor
          : dataset.backgroundColor,
        type: dataset.type,
      };
    });

    if (isRadar && dataPointIndex !== undefined) {
      const allDataPoints = chart.data.datasets
        .map((dataset: any) => {
          if (
            dataset.label === t('phishing.analytics.signsWithSimulation') ||
            dataset.label === 'No Data'
          ) {
            return null;
          }

          const value = dataset.data[dataPointIndex];
          if (value === null || value === undefined) {
            return null;
          }

          return {
            label: dataset.label,
            value: value,
            color:
              dataset.backgroundColor[dataPointIndex] || dataset.borderColor,
            type: dataset.type || 'line',
          };
        })
        .filter(Boolean);

      dataPoints.length = 0;
      dataPoints.push(...allDataPoints);
    }

    let existingRoot = tooltipRoots.get(tooltipEl);
    if (!existingRoot) {
      existingRoot = createRoot(tooltipEl);
      tooltipRoots.set(tooltipEl, existingRoot);
    }
    const root = existingRoot;

    const legendData = isRadar
      ? dataPoints.filter(
          (p: any) =>
            p.label !== 'Signs with simulation' && p.label !== 'No Data',
        )
      : isLineChart
      ? dataPoints
      : dataPoints.filter((p: any) => p.type === 'bar');

    flushSync(() => {
      root.render(
        <TooltipContent
          title={title}
          dataPoints={legendData}
          emailsSent={emailsSent}
          t={t}
          isRadar={isRadar}
          showPercentage={showPercentage}
          isOverdueTraining={isOverdueTraining}
        />,
      );
    });
  }

  tooltipEl.style.whiteSpace = 'nowrap';
  tooltipEl.style.minWidth = 'max-content';

  if (!chart.canvas || !chart?.canvas?.parentNode) {
    tooltipEl.style.opacity = '0';
    return;
  }

  const canvasRect = chart.canvas.getBoundingClientRect();

  if (!chart?.canvas?.parentNode) return;

  if (isRadar) {
    const mouseX = tooltip.caretX;
    const mouseY = tooltip.caretY || tooltip.y;

    const estimatedWidth = 200;
    const estimatedHeight = 100;
    const padding = 10;

    let left = canvasRect.left + mouseX + padding;
    let top = canvasRect.top + mouseY + padding;

    if (left + estimatedWidth > window.innerWidth) {
      left = canvasRect.left + mouseX - estimatedWidth - padding;
    }
    if (top + estimatedHeight > window.innerHeight) {
      top = canvasRect.top + mouseY - estimatedHeight - padding;
    }
    if (left < padding) {
      left = padding;
    }
    if (top < padding) {
      top = padding;
    }

    tooltipEl.style.left = left + 'px';
    tooltipEl.style.top = top + 'px';
    tooltipEl.style.transform = 'none';
  } else {
    const caretX = tooltip.caretX;
    const viewportX = canvasRect.left + caretX;
    const viewportY = canvasRect.top;
    const tooltipWidth = tooltipEl.offsetWidth;

    const centeredLeft = viewportX - tooltipWidth / 2;
    const centeredRight = viewportX + tooltipWidth / 2;

    let transform = 'translateX(-50%)';
    if (centeredLeft < 0) {
      transform = 'translateX(0%)';
    } else if (centeredRight > window.innerWidth) {
      transform = 'translateX(-100%)';
    }

    tooltipEl.style.left = viewportX + 'px';
    tooltipEl.style.top = viewportY + 'px';
    tooltipEl.style.transform = transform;
  }

  tooltipEl.style.opacity = '1';
};

export const tooltipDefaultSettings = () => {
  const docStyle = getComputedStyle(document.documentElement);
  return {
    displayColors: false,
    padding: {
      top: xSmallPadding(docStyle).replace('px', ''),
      bottom: xSmallPadding(docStyle).replace('px', ''),
      left: mediumPadding(docStyle).replace('px', ''),
      right: mediumPadding(docStyle).replace('px', ''),
    },
    backgroundColor: colorPurpleDark(docStyle),
    cornerRadius: xxxSmallBorderRadius(docStyle)?.replace('px', ''),
    bodyFont: {
      size: xSmallFontSize(docStyle),
      family: platformFontFamily(docStyle),
    },
  };
};

export const phishingBarTooltip = () => {
  const docStyle = getComputedStyle(document.documentElement);
  return {
    ...tooltipDefaultSettings(),
    padding: xSmallPadding(docStyle).replace('px', ''),
    bodyFont: {
      size: xSmallPadding(docStyle).replace('px', ''),
    },
  };
};

export const getClickedBackgroundColors = (
  labels: string[],
  docStyle: CSSStyleDeclaration,
) => {
  return labels.map(() => `${colorGreenRadarDarkBorder(docStyle)}80`);
};

export const getSubmittedBackgroundColors = (
  labels: string[],
  docStyle: CSSStyleDeclaration,
) => {
  return labels.map(() => `${colorGreenRadarMiddleBorder(docStyle)}80`);
};

export const getReportedBackgroundColors = (
  labels: string[],
  docStyle: CSSStyleDeclaration,
) => {
  return labels.map(() => `${colorGreenRadarLightBorder(docStyle)}80`);
};

export const getClickedBorderColors = (
  labels: string[],
  docStyle: CSSStyleDeclaration,
) => {
  return labels.map(() => colorGreenRadarDarkBorder(docStyle));
};

export const getSubmittedBorderColors = (
  labels: string[],
  docStyle: CSSStyleDeclaration,
) => {
  return labels.map(() => colorGreenRadarMiddleBorder(docStyle));
};

export const getReportedBorderColors = (
  labels: string[],
  docStyle: CSSStyleDeclaration,
) => {
  return labels.map(() => colorGreenRadarLightBorder(docStyle));
};

export const getBackgroundColors = (
  labels: string[],
  docStyle: CSSStyleDeclaration,
) => {
  return labels.map(() => `${colorGrayPale(docStyle)}B3`);
};

export const calculateChartMaxValue = (dataValues: any[]): number => {
  const cappedDataValues = dataValues.map((value: any) =>
    Math.min(Number(value), 100),
  );

  const allValues = cappedDataValues.filter(
    (val): val is number =>
      val !== null && val !== undefined && !isNaN(val) && isFinite(val),
  );

  const maxDataValue = allValues.length > 0 ? Math.max(...allValues) : 0;
  const stepSize = 20;

  const roundedMax = Math.min(
    Math.ceil(maxDataValue / stepSize) * stepSize,
    100,
  );
  return roundedMax;
};

export const renderCustomXAxis = (
  data: any[],
  isOverdue: boolean,
  selectedMetric?: string,
) => {
  let transformedData: number[] = [];
  if (isOverdue) {
    const { dataValues } = getGroupedOverdueCoursesChartData(data);
    transformedData = dataValues;
  } else if (selectedMetric) {
    const { dataValues } = getDepartmentsPerformanceChartData(
      data,
      selectedMetric,
    );
    transformedData = dataValues;
  }

  const dynamicMax = calculateChartMaxValue(transformedData);

  const stepSize = 20;

  const labels: number[] = [];

  for (let i = 0; i <= dynamicMax; i += stepSize) {
    labels.push(i);
  }

  if (labels.length > 0 && labels[labels.length - 1] < dynamicMax) {
    labels.push(dynamicMax);
  }

  return labels.map((value) => (
    <span key={value}>
      {value}
      {isOverdue ? '' : '%'}
    </span>
  ));
};

export const createTextWrappingCallback = (
  data: any[],
  isOverdue?: boolean,
  selectedMetric?: string,
) => {
  return (value: any, index: number) => {
    let labels: string[] = [];
    if (isOverdue) {
      const { dataLabels } = getGroupedOverdueCoursesChartData(data);
      labels = dataLabels;
    } else if (selectedMetric) {
      const { dataLabels } = getDepartmentsPerformanceChartData(
        data,
        selectedMetric,
      );
      labels = dataLabels;
    }

    const label = labels?.[index] || value?.toString() || '';
    const maxLineLength = 24;

    if (label.length <= maxLineLength) {
      return label;
    }

    const words = label.split(' ');
    if (words.length === 1) {
      if (label.length <= maxLineLength * 2) {
        return [
          label.substring(0, maxLineLength),
          label.substring(maxLineLength),
        ];
      }
      return [
        label.substring(0, maxLineLength),
        label.substring(maxLineLength, maxLineLength * 2 - 3) + '...',
      ];
    }
    let firstLine = '';
    let secondLine = '';

    for (const word of words) {
      if ((firstLine + ' ' + word).trim().length <= maxLineLength) {
        firstLine = (firstLine + ' ' + word).trim();
      } else {
        secondLine = (secondLine + ' ' + word).trim();
        const hasEllipsis = secondLine.length > maxLineLength;
        if (hasEllipsis) {
          secondLine = secondLine.substring(0, maxLineLength) + '...';
        }
      }
    }
    if (secondLine) {
      return [firstLine, secondLine];
    }

    return label;
  };
};

export const initCustomMiddlePositionForTooltip = () => {
  const positioners = Tooltip.positioners as any;
  positioners.middle = (items: any) => {
    if (items.length !== 1) {
      return positioners.average(items);
    }

    const el = items[0].element;
    let xPos = 0;
    let yPos = 0;

    if (el && el.hasValue()) {
      const { base, horizontal, x, y } = el.getProps();

      if (horizontal) {
        xPos = (base + x) / 2;
        yPos = y;
      } else {
        xPos = x;
        yPos = (base + y) / 2;
      }
    }

    return {
      x: xPos,
      y: yPos,
    };
  };
};

export const getVulnerableUsersDataMappedToLabels = (
  reportData: VulnerableUsersOverTimeReport[] | undefined,
  labels: { [key: number]: string }[],
  isYearly?: boolean,
): {
  label: string;
  value: VulnerableUsersOverTimeReport | null;
}[] => {
  if (!reportData || !labels) {
    return [];
  }

  if (isYearly) {
    return labels.map((labelObject: { [key: number]: string }) => {
      const matchingItem = (
        reportData as VulnerableUsersOverTimeReport[]
      )?.find((item: any) => {
        return Object.values(labelObject).includes(item.period);
      });
      return {
        label: Object.values(labelObject)[0],
        value: matchingItem || null,
      };
    });
  } else {
    return labels.map((labelObject: { [key: number]: string }) => {
      const matchingItem = (
        reportData as VulnerableUsersOverTimeReport[]
      )?.find((item: any) => {
        const month = Number(item?.period?.split('-')[0]) - 1;
        return month === Number(Object.keys(labelObject)[0]);
      });
      return {
        label: Object.values(labelObject)[0],
        value: matchingItem || null,
      };
    });
  }
};

const getMonthIndexFromIsoPeriod = (
  period: string | undefined,
): number | null => {
  if (!period) return null;

  // Expected format: "YYYY-MM-DDTHH:mm:ss.sssZ"
  const date = new Date(period);
  if (isNaN(date.getTime())) {
    return null;
  }

  return date.getMonth();
};

export const getChartTranslations = (
  isCompletionsOverTime: boolean,
  t: TranslationFunctionType,
) => {
  if (isCompletionsOverTime) {
    return {
      mainLabel: t('dashboard.awareness.completions.title'),
      trendLabel: t('phishing.simulation.completionsTrend'),
    };
  }

  return {
    mainLabel: t('phishing.simulation.riskyUsers'),
    trendLabel: t('phishing.simulation.riskyUsersTrend'),
  };
};

export const getCompletionsDataMappedToLabels = (
  reportData: CompletionsOverTimeReport[] | undefined,
  labels: { [key: number]: string }[],
): {
  label: string;
  value: CompletionsOverTimeReport | null;
}[] => {
  if (!reportData || !labels) {
    return [];
  }

  return labels.map((labelObject: { [key: number]: string }) => {
    const matchingItem = reportData?.find((item: any) => {
      const monthIndex = getMonthIndexFromIsoPeriod(item?.period as string);
      return (
        monthIndex !== null &&
        monthIndex === Number(Object.keys(labelObject)[0])
      );
    });

    return {
      label: Object.values(labelObject)[0],
      value: matchingItem || null,
    };
  });
};

export const createLineChartGradient = (
  isCompletionsOverTime: boolean,
  docStyle: CSSStyleDeclaration,
): CanvasGradient | null => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const gradient = isCompletionsOverTime
    ? ctx.createLinearGradient(0, 0, 0, 320)
    : ctx.createLinearGradient(0, 0, 0, 400);

  const mainColor = isCompletionsOverTime
    ? colorBlueDark(docStyle)
    : colorRedMain(docStyle);

  gradient.addColorStop(0, mainColor);

  if (isCompletionsOverTime) {
    gradient.addColorStop(0.7, 'rgba(63, 157, 223, 0.3)');
    gradient.addColorStop(1, 'rgba(63, 157, 223, 0)');
  } else {
    gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
  }

  return gradient;
};

export const calculateLineChartYAxisMax = (
  filteredDatasets: {
    label: string;
    value: VulnerableUsersOverTimeReport | CompletionsOverTimeReport | null;
  }[],
  isCompletionsOverTime: boolean,
): number => {
  const maxValue =
    filteredDatasets?.reduce((max, item: any) => {
      if (isCompletionsOverTime) {
        if (
          item.value?.cumulativeEnrollments &&
          item.value?.cumulativeCompleted
        ) {
          const completionRate = Number(
            (
              (item.value.cumulativeCompleted /
                item.value.cumulativeEnrollments) *
              100
            ).toFixed(2),
          );
          return Math.max(max, completionRate);
        }
        return max;
      } else {
        return Math.max(max, item.value?.percentageRiskyUsers || 0);
      }
    }, 0) || 0;

  if (isCompletionsOverTime) {
    return Math.ceil(maxValue / 10) * 10 || 10;
  }

  return Math.ceil(maxValue / 5) * 5;
};

export const overdueDemoChartData = [
  {
    period: '2025-11-01 00:00:00+00',
    usersCount: 17,
  },
  {
    period: '2025-10-01 00:00:00+00',
    usersCount: 14,
  },
  {
    period: '2025-09-01 00:00:00+00',
    usersCount: 11,
  },
  {
    period: '2025-08-01 00:00:00+00',
    usersCount: 9,
  },
  {
    period: '2025-07-01 00:00:00+00',
    usersCount: 12,
  },
  {
    period: '2025-06-01 00:00:00+00',
    usersCount: 16,
  },
];

export const createBarChartTooltip = (
  tooltipId: string,
  tooltipStyles: ReturnType<typeof tooltipDefaultSettings>,
): HTMLElement => {
  const existing = document.getElementById(tooltipId) as HTMLElement;
  if (existing) return existing;

  const el = document.createElement('div');
  el.id = tooltipId;
  const bg = tooltipStyles.backgroundColor as string;
  const p = tooltipStyles.padding;

  Object.assign(el.style, {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: '10000',
    display: 'none',
    backgroundColor: bg,
    borderRadius: `${tooltipStyles.cornerRadius}px`,
    padding: `${p.top}px ${p.right}px ${p.bottom}px ${p.left}px`,
    fontSize: `${parseInt(tooltipStyles.bodyFont.size as string, 10)}px`,
    fontFamily: tooltipStyles.bodyFont.family as string,
    color: 'white',
    whiteSpace: 'nowrap',
  });

  el.style.setProperty('--tooltip-bg', bg);
  const s = document.createElement('style');
  s.textContent = `#${tooltipId}::after{content:'';position:absolute;bottom:-6px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:6px solid var(--tooltip-bg);}`;
  document.head.appendChild(s);
  document.body.appendChild(el);
  return el;
};

export const hideTooltip = (tooltipEl: HTMLElement) => {
  tooltipEl.style.display = 'none';
};

export const showTooltip = (
  tooltipEl: HTMLElement,
  label: string,
  chart: any,
  tooltipContext: any,
) => {
  if (!chart?.canvas || !tooltipEl) return;

  tooltipEl.textContent = label;
  tooltipEl.style.display = 'block';

  requestAnimationFrame(() => {
    if (!tooltipEl) return;

    const cr = chart.canvas.getBoundingClientRect();
    const tr = tooltipEl.getBoundingClientRect();
    const x = tooltipContext.caretX || tooltipContext.x || 0;
    tooltipEl.style.left = `${cr.left + x - tr.width / 2}px`;
    tooltipEl.style.top = `${cr.top + cr.height / 2 - tr.height / 2 + 35}px`;
  });
};
